import type { SplitCostingRow } from "./parseSplitCostingCsv";

/**
 * Portal-only warning identifiers — used when
 * `enable-split-costing-datapay-aware-validations` is **off**. When the flag
 * is on, the report renders API `result.warnings` instead (AC-APP-07) and the
 * server blocks over-100% (AC-APP-04 / R-APP-4).
 */
export type SplitCostingWarningType = "AC-APP-04" | "AC-APP-07";

/** One portal warning requiring acknowledgement before Continue to commit. */
export interface SplitCostingImportWarning {
  /** Stable batch identity shared by every affected row in the clustered report. */
  id: string;
  type: SplitCostingWarningType;
  employeeCode: string;
  message: string;
  /** File row numbers (1-based) this warning is about, for traceability in the UI. */
  fileRows: number[];
}

/**
 * Options for `computeSplitCostingWarnings`.
 */
export interface ComputeSplitCostingWarningsOptions {
  /**
   * When `true`, skip the AC-APP-04 over-allocation check.  Set this when the
   * `enable-split-costing-datapay-aware-validations` feature flag is on:
   * the server applies the R-APP-4 interval sweep and returns a blocking error
   * instead, so the client warning would duplicate (and incorrectly frame as
   * non-blocking) a check the server already blocks.
   */
  skipOverAllocation?: boolean;
}

interface SplitCostingApiWarningLike {
  employeeCode: string | null;
  indices: number[];
  warning: string;
}

function warningSignatures(
  warnings: readonly SplitCostingImportWarning[]
): ReadonlyMap<string, string> {
  return new Map(
    warnings.map((warning) => [warning.id, warning.message] as const)
  );
}

/**
 * Preserve acknowledgements only while both warning identity and message are
 * unchanged. A row edit that changes the affected set or warning total must
 * be reviewed again, even when its stable row-based ID remains the same.
 */
export function reconcileSplitCostingWarningAcknowledgements(
  dismissed: ReadonlySet<string>,
  previous: readonly SplitCostingImportWarning[],
  next: readonly SplitCostingImportWarning[]
): ReadonlySet<string> {
  const previousSignatures = warningSignatures(previous);
  const nextSignatures = warningSignatures(next);
  return new Set(
    [...dismissed].filter(
      (id) =>
        previousSignatures.get(id) !== undefined &&
        previousSignatures.get(id) === nextSignatures.get(id)
    )
  );
}

/**
 * Convert narrowed API warnings into the same row-based shape used by the
 * clustered report. Warnings with no valid row index are ignored so they
 * cannot block acknowledgement without a visible source row.
 */
export function mapSplitCostingApiWarnings(
  apiWarnings: readonly SplitCostingApiWarningLike[],
  rows: readonly SplitCostingRow[]
): SplitCostingImportWarning[] {
  return apiWarnings.flatMap((apiWarning) => {
    const fileRows = apiWarning.indices
      .map((index) => rows[index]?.fileRow)
      .filter((fileRow): fileRow is number => typeof fileRow === "number");
    if (fileRows.length === 0) return [];

    const employeeCode = apiWarning.employeeCode ?? "";
    return [
      {
        id: warningId("AC-APP-07", employeeCode, fileRows),
        type: "AC-APP-07" as const,
        employeeCode,
        message: `${apiWarning.warning} (rows ${fileRows.join(", ")})`,
        fileRows,
      },
    ];
  });
}

function joinPath(row: SplitCostingRow): string {
  return row.path.map((level) => level.code).join("/");
}

/** `null`/non-numeric → not a decimal (AC-ERR-04 owns that as a blocking error). */
function parsePercentage(raw: string): number | null {
  const value = Number(raw);
  return raw.trim() !== "" && Number.isFinite(value) ? value : null;
}

function warningId(
  type: SplitCostingWarningType,
  employeeCode: string,
  fileRows: number[]
): string {
  return `${type}:${employeeCode}:${[...fileRows].sort((a, b) => a - b).join(",")}`;
}

/**
 * AC-APP-04 — rows for the same employee whose Apportioned Percentage sums to
 * **more than 100%** produce a warning naming every contributing row. A sum
 * of 100% or less (including 80%) produces no output at all — that silence is
 * load-bearing, see the plan's negative-test list.
 *
 * Rows with a missing/non-decimal percentage are skipped here — AC-ERR-04
 * already reports those as a blocking error; double-reporting as a warning
 * would be noise.
 */
function computeOverAllocationWarnings(
  rows: SplitCostingRow[]
): SplitCostingImportWarning[] {
  const byEmployee = new Map<string, SplitCostingRow[]>();
  for (const row of rows) {
    const bucket = byEmployee.get(row.employeeCode);
    if (bucket) bucket.push(row);
    else byEmployee.set(row.employeeCode, [row]);
  }

  const warnings: SplitCostingImportWarning[] = [];
  for (const [employeeCode, employeeRows] of byEmployee) {
    const percentages = employeeRows.map((row) =>
      parsePercentage(row.apportionedPercentage)
    );
    if (percentages.some((value) => value === null)) continue;

    const rawSum = (percentages as number[]).reduce((a, b) => a + b, 0);
    const sum = Math.round(rawSum * 1e10) / 1e10;
    if (sum > 100) {
      const fileRows = employeeRows.map((row) => row.fileRow);
      warnings.push({
        id: warningId("AC-APP-04", employeeCode, fileRows),
        type: "AC-APP-04",
        employeeCode,
        message:
          `Total apportioned percentage for employee ${employeeCode} is ` +
          `${sum}% across rows ${employeeRows.map((r) => r.fileRow).join(", ")} — exceeds 100%.`,
        fileRows,
      });
    }
  }
  return warnings;
}

/**
 * Inclusive date-range overlap check. A `null` start is treated as
 * unbounded-in-the-past; a `null` end is unbounded-in-the-future — which is
 * the common case here, since `endDate` is typically `null` (most rows have
 * no end date) — any two rows for the same employee/destination will typically
 * overlap regardless of start date.
 */
function rangesOverlap(
  aStart: string | null,
  aEnd: string | null,
  bStart: string | null,
  bEnd: string | null
): boolean {
  const startsOnOrBefore = (start: string | null, end: string | null) =>
    start === null || end === null || start <= end;
  return startsOnOrBefore(aStart, bEnd) && startsOnOrBefore(bStart, aEnd);
}

/**
 * AC-APP-07 — two or more upload rows sharing the same Employee Code and the
 * same resolved split destination (path), with overlapping date ranges,
 * produce a warning. No dedupe — every row is still submitted as-is.
 *
 * Flag-off fallback only. When Datapay-aware validations are on, the API
 * owns AC-APP-07 (including create-vs-standing overlap).
 */
function computeDuplicateDestinationWarnings(
  rows: SplitCostingRow[]
): SplitCostingImportWarning[] {
  const byDestination = new Map<string, SplitCostingRow[]>();
  for (const row of rows) {
    const key = `${row.employeeCode}::${joinPath(row)}`;
    const bucket = byDestination.get(key);
    if (bucket) bucket.push(row);
    else byDestination.set(key, [row]);
  }

  const warnings: SplitCostingImportWarning[] = [];
  for (const bucket of byDestination.values()) {
    if (bucket.length < 2) continue;

    const overlapping = new Set<SplitCostingRow>();
    for (let i = 0; i < bucket.length; i++) {
      for (let j = i + 1; j < bucket.length; j++) {
        const a = bucket[i];
        const b = bucket[j];
        if (rangesOverlap(a.startDate, a.endDate, b.startDate, b.endDate)) {
          overlapping.add(a);
          overlapping.add(b);
        }
      }
    }

    if (overlapping.size >= 2) {
      const involved = bucket.filter((row) => overlapping.has(row));
      const fileRows = involved.map((row) => row.fileRow);
      warnings.push({
        id: warningId("AC-APP-07", involved[0].employeeCode, fileRows),
        type: "AC-APP-07",
        employeeCode: involved[0].employeeCode,
        message:
          `Employee ${involved[0].employeeCode} has ${involved.length} rows ` +
          `splitting to ${joinPath(involved[0])} with overlapping dates ` +
          `(rows ${involved.map((r) => r.fileRow).join(", ")}).`,
        fileRows,
      });
    }
  }
  return warnings;
}

/**
 * Compute the portal-only warnings (AC-APP-04, AC-APP-07) client-side from
 * the uploaded rows. Used when `enable-split-costing-datapay-aware-validations`
 * is **off**. When the flag is on, {@link SplitCostingValidationReport}
 * renders API `result.warnings` instead and skips this function.
 *
 * Pure function — no React, no side effects.
 */
export function computeSplitCostingWarnings(
  rows: SplitCostingRow[],
  options: ComputeSplitCostingWarningsOptions = {}
): SplitCostingImportWarning[] {
  return [
    ...(options.skipOverAllocation ? [] : computeOverAllocationWarnings(rows)),
    ...computeDuplicateDestinationWarnings(rows),
  ];
}
